import { Controller, useFormContext } from "react-hook-form";

import Banner from "ds/components/Banner";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import { BlueprintInput, BlueprintInputType } from "types/generated";

import { VersionTemplateFormFields } from "./types";

type DeploymentFormFieldProps = {
  input: BlueprintInput;
  editMode?: boolean;
};

const DeploymentFormField = ({ input, editMode }: DeploymentFormFieldProps) => {
  const { register, control } = useFormContext<VersionTemplateFormFields["inputs"]>();

  switch (input.type) {
    case BlueprintInputType.ShortText: {
      return (
        <FormField key={input.id} label={input.name} helperText={input.description} noMargin>
          {({ ariaInputProps }) => (
            <Input {...register(`inputs.${input.id}`)} {...ariaInputProps} />
          )}
        </FormField>
      );
    }
    case BlueprintInputType.LongText: {
      return (
        <FormField key={input.id} label={input.name} helperText={input.description} noMargin>
          {({ ariaInputProps }) => (
            <Textarea {...register(`inputs.${input.id}`)} {...ariaInputProps} />
          )}
        </FormField>
      );
    }
    case BlueprintInputType.Secret: {
      return (
        <>
          {editMode && (
            <Banner variant="danger">
              Below field contained sensitive information that were removed for security reasons.
              You need to provide a new value, otherwise it will be removed.
            </Banner>
          )}
          <FormField key={input.id} label={input.name} helperText={input.description} noMargin>
            {({ ariaInputProps }) => (
              <Input
                type="password"
                {...register(`inputs.${input.id}`)}
                {...ariaInputProps}
                error={editMode}
                placeholder={editMode ? "Leave blank to remove secret" : undefined}
              />
            )}
          </FormField>
        </>
      );
    }
    case BlueprintInputType.Number: {
      return (
        <FormField key={input.id} label={input.name} helperText={input.description} noMargin>
          {({ ariaInputProps }) => (
            <Input type="number" {...register(`inputs.${input.id}`)} {...ariaInputProps} />
          )}
        </FormField>
      );
    }
    case BlueprintInputType.Select: {
      return (
        <Controller
          name={`inputs.${input.id}`}
          control={control}
          render={({ field }) => (
            <ComboBox
              key={input.id}
              label={input.name}
              helperText={input.description}
              items={input.options.map((option) => ({ value: option, label: option }))}
              onChange={field.onChange}
              value={field.value as string}
            >
              {(item) => <ComboBoxDefaultItem id={item.value} label={item.label} />}
            </ComboBox>
          )}
        />
      );
    }
    case BlueprintInputType.Float: {
      return (
        <FormField key={input.id} label={input.name} helperText={input.description} noMargin>
          {({ ariaInputProps }) => (
            <Input
              type="number"
              step="any"
              {...register(`inputs.${input.id}`)}
              {...ariaInputProps}
            />
          )}
        </FormField>
      );
    }
    case BlueprintInputType.Boolean: {
      return (
        <Controller
          name={`inputs.${input.id}`}
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              id={`inputs.${input.id}`}
              onChange={field.onChange}
              checked={field.value as boolean}
              aria-label={
                field.value
                  ? `Unselect ${input.description || input.name}`
                  : `Select ${input.description || input.name}`
              }
              title={input.name}
              description={input.description}
            />
          )}
        />
      );
    }
  }
};

export default DeploymentFormField;
